import { jsx, jsxs } from "react/jsx-runtime";
import React__default, { useRef, useState, useEffect, createElement } from "react";
import { Box, Carousel, StyledCarouselScrollContainer, token, StyledCarouselSlide, IconButton, IconChevronLeft, IconChevronRight } from "@zillow/constellation";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import styled from "styled-components";
import ThemeCmsConstellation from "../../themes/ThemeCmsConstellation.esm.js";
import PostCard from "../PostCard/PostCard.esm.js";
const StyledCarousel = styled(Carousel).withConfig({
  displayName: "PostsLayout__StyledCarousel",
  componentId: "sc-1k647xh-0"
})(["", "{margin:0 -", "px;}", "{flex-basis:", ";}"], StyledCarouselScrollContainer, token("spacing.sm"), StyledCarouselSlide, (props) => Number.isInteger(props.slideWidth) ? `${props.slideWidth}px` : props.slideWidth);
const CustomCarousel = ({
  children,
  ...props
}) => /* @__PURE__ */ jsx(Box, { sx: {
  margin: "-18px 0 $spacing.lg 0"
}, children: /* @__PURE__ */ jsx(StyledCarousel, { ...props, children }) });
CustomCarousel.propTypes = {
  children: PropTypes.node.isRequired
};
function getViewPort(view) {
  if (view.innerWidth < 768) {
    return "mobile";
  }
  if (view.innerWidth < 1024) {
    return "tablet";
  }
  return "desktop";
}
const PostsLayout = ({
  layout,
  items
}) => {
  const ref = useRef();
  const [currentViewPort, setCurrentViewPort] = useState("desktop");
  const layouts = {
    GRID: {
      "> *": {
        gridColumnEnd: "span 6"
      },
      "@media $md_lte": {
        display: "block"
      }
    },
    MOSAIC: {
      "> *:nth-child(1)": {
        gridColumnEnd: "span 6",
        gridRowEnd: `span ${items.length - 1}`
      },
      "> *:nth-child(n+2)": {
        gridColumnEnd: "span 6"
      },
      "@media $lg_lte": {
        "> *:nth-child(1)": {
          gridColumnEnd: "span 12",
          gridRowEnd: "span 1"
        },
        "> *:nth-child(n+2)": {
          gridColumnEnd: "span 12"
        }
      }
    },
    INLINE: {
      display: items.length > 3 ? "block" : "grid",
      "> *": {
        gridColumnEnd: "span 4"
      },
      "@media $lg_lte": {
        display: "block"
      }
    },
    CAROUSEL: {
      display: "block"
    }
  };
  useEffect(() => {
    const {
      ownerDocument
    } = ref.current;
    const {
      defaultView
    } = ownerDocument;
    const onResize = () => {
      setCurrentViewPort(getViewPort(defaultView));
    };
    const debouncedOnResize = debounce(onResize, 100);
    setCurrentViewPort(getViewPort(defaultView));
    defaultView.addEventListener("resize", debouncedOnResize);
    defaultView.addEventListener("orientationchange", debouncedOnResize);
    return () => {
      defaultView.removeEventListener("resize", debouncedOnResize);
      defaultView.removeEventListener("orientationchange", debouncedOnResize);
    };
  }, []);
  let PostsItems = () => items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id }));
  const renderControls = (leftButtonProps, rightButtonProps) => {
    if (currentViewPort !== "mobile") {
      return /* @__PURE__ */ jsxs(Box, { sx: {
        position: "absolute",
        top: "100%",
        right: "0",
        display: "grid",
        gridAutoFlow: "column",
        gridColumnGap: "$spacing.sm",
        alignItems: "start"
      }, children: [
        /* @__PURE__ */ jsx(IconButton, { "aria-hidden": true, appearance: "circle", "aria-label": "Previous", icon: /* @__PURE__ */ jsx(IconChevronLeft, {}), size: "sm", tabIndex: "-1", title: "Previous items", ...leftButtonProps }),
        /* @__PURE__ */ jsx(IconButton, { "aria-hidden": true, appearance: "circle", "aria-label": "Next", icon: /* @__PURE__ */ jsx(IconChevronRight, {}), size: "sm", tabIndex: "-1", title: "Next items", ...rightButtonProps })
      ] });
    }
    return null;
  };
  if (layout === "MOSAIC") {
    PostsItems = function PostItems() {
      return /* @__PURE__ */ jsxs(React__default.Fragment, { children: [
        /* @__PURE__ */ jsx(PostCard, { ...items[0], alignment: "vertical", size: "lg" }),
        currentViewPort === "mobile" || currentViewPort === "tablet" ? /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth: 320, enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.slice(1).map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "md", excerpt: void 0 })) }) : items.slice(1).map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "md", excerpt: void 0 }))
      ] });
    };
  }
  if (layout === "INLINE") {
    PostsItems = function PostItems() {
      return currentViewPort === "mobile" || currentViewPort === "tablet" || items.length > 3 ? /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth: 320, enableSnap: true, slideAlign: "start", render: true, scrollOffset: "sm", children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "vertical", size: "md", excerpt: void 0 })) }) : items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "vertical", size: "md", excerpt: void 0 }));
    };
  }
  if (layout === "CAROUSEL") {
    PostsItems = function PostsItems2() {
      if (items.length <= 1) {
        return /* @__PURE__ */ createElement(PostCard, { ...items[0], key: items[0].id, alignment: "horizontal", size: "xl" });
      }
      const slideWidth = currentViewPort === "mobile" ? `calc(100% - ${ThemeCmsConstellation.constellation.spacing.sm}px)` : "100%";
      return /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth, enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "xl" })) });
    };
  }
  if (layout === "GRID") {
    PostsItems = function PostsItems2() {
      return currentViewPort === "mobile" ? /* @__PURE__ */ jsx(CustomCarousel, { renderNavigationControls: renderControls, slideWidth: 320, enableSnap: true, slideAlign: "start", scrollOffset: "sm", children: items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "md", excerpt: void 0 })) }) : items.map((item) => /* @__PURE__ */ createElement(PostCard, { ...item, key: item.id, alignment: "horizontal", size: "md", excerpt: void 0 }));
    };
  }
  return /* @__PURE__ */ jsx(Box, { ref, sx: {
    display: "grid",
    gridTemplateColumns: "repeat(12, 1fr)",
    gridGap: "$spacing.md",
    ...layouts[layout]
  }, children: /* @__PURE__ */ jsx(PostsItems, {}) });
};
PostsLayout.propTypes = {
  layout: PropTypes.oneOf(["GRID", "MOSAIC", "INLINE", "CAROUSEL"]).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape(PostCard.propTypes))
};
PostsLayout.defaultProps = {
  items: []
};
export {
  PostsLayout as default
};
